import PocketBase from 'pocketbase'
import { isMaintenanceResponse, redirectToMaintenance } from './maintenance'

export const pb = new PocketBase(import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai')

// Route users to the blocker page when the backend is in maintenance mode
// (503 {"maintenance": true} from the maintenance_mode middleware).
pb.afterSend = (response, data) => {
  if (isMaintenanceResponse(response.status, data)) {
    redirectToMaintenance()
  }
  return data
}

// Types
export interface User {
  id: string
  email: string
  name?: string
  username?: string
  avatar?: string
  created: string
  updated: string
  points_balance?: number
  role?: 'student' | 'teacher'
  points_to_distribute?: number
  gender?: 'male' | 'female'
  reading_power?: number
  reading_power_last_calculated?: string
  access_shop?: boolean
  has_seen_shop_promo?: boolean
}

export interface AuthResponse {
  record: User
  token: string
}

export class AuthError extends Error {
  status: number
  data?: Record<string, any>

  constructor({ message, status, data }: { message: string; status: number; data?: Record<string, any> }) {
    super(message)
    this.name = 'AuthError'
    this.status = status
    this.data = data
  }
}

// Enhanced auth functions with better error handling
export const authUser = async (email: string, password: string): Promise<AuthResponse> => {
  try {
    const result = await pb.collection('users').authWithPassword(email, password)
    return result as unknown as AuthResponse
  } catch (error: any) {
    throw new AuthError({
      message: error?.message || 'Authentication failed',
      status: error?.status || 500,
      data: error?.data || {},
    })
  }
}

export const createUser = async (
  email: string,
  password: string,
  passwordConfirm: string,
  name?: string,
  username?: string,
  birthdate?: string,
  gender?: 'male' | 'female'
): Promise<User> => {
  try {
    const result = await pb.collection('users').create({
      email,
      password,
      passwordConfirm,
      name,
      username,
      birthdate,
      gender,
    })
    return result as unknown as User
  } catch (error: any) {
    // PocketBase error structure includes response.data for field errors
    const errorData = error?.response?.data || error?.data || {}

    throw new AuthError({
      message: error?.message || 'Registration failed',
      status: error?.status || 500,
      data: errorData,
    })
  }
}

export const signOut = (): void => {
  pb.authStore.clear()
}

export const getCurrentUser = (): User | null => {
  return pb.authStore.model as unknown as User | null
}

export const isAuthenticated = (): boolean => {
  return pb.authStore.isValid && !!pb.authStore.model
}

export const getAuthToken = (): string => {
  return pb.authStore.token
}

export const refreshAuth = async (): Promise<AuthResponse | null> => {
  try {
    if (!pb.authStore.isValid) {
      return null
    }
    const result = await pb.collection('users').authRefresh()
    return result as unknown as AuthResponse
  } catch (error) {
    // If refresh fails, clear the auth store
    pb.authStore.clear()
    return null
  }
}

// Auth store listener
export const onAuthChange = (callback: (token: string, model: User | null) => void) => {
  return pb.authStore.onChange((token, model) => {
    callback(token, model as unknown as User | null)
  })
}

// Password reset functions
export const requestPasswordReset = async (email: string): Promise<void> => {
  try {
    await pb.collection('users').requestPasswordReset(email)
  } catch (error: any) {
    throw new AuthError({
      message: error?.message || 'Password reset request failed',
      status: error?.status || 500,
      data: error?.data || {},
    })
  }
}

export const confirmPasswordReset = async (
  token: string,
  password: string,
  passwordConfirm: string
): Promise<void> => {
  try {
    await pb.collection('users').confirmPasswordReset(token, password, passwordConfirm)
  } catch (error: any) {
    throw new AuthError({
      message: error?.message || 'Password reset confirmation failed',
      status: error?.status || 500,
      data: error?.data || {},
    })
  }
}

export const signInWithGoogle = async (): Promise<void> => {
  try {
    await pb.collection('users').authWithOAuth2({ 
      provider: 'google'
    })
  } catch (error: any) {
    throw new AuthError({
      message: error?.message || 'Google sign-in failed',
      status: error?.status || 500,
      data: error?.data || {},
    })
  }
}

// Helper function to get file URL
export const getFileUrl = (collectionId: string, recordId: string, filename: string): string => {
  const baseUrl = import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai'
  return `${baseUrl}/api/files/${collectionId}/${recordId}/${filename}`
}

// SSO function to redirect to shop
export const generateShopSSOToken = async (): Promise<string> => {
  try {
    const baseUrl = import.meta.env.VITE_POCKETBASE_URL || 'https://pb.mybooq.ai'
    console.log('[SSO] Starting token generation...')
    console.log('[SSO] Base URL:', baseUrl)
    console.log('[SSO] Auth valid:', pb.authStore.isValid)
    console.log('[SSO] Has token:', !!pb.authStore.token)

    const response = await fetch(`${baseUrl}/api/sso/generate-shop-token`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${pb.authStore.token}`,
      },
    })

    console.log('[SSO] Response status:', response.status)
    console.log('[SSO] Response ok:', response.ok)

    if (!response.ok) {
      const errorData = await response.json()
      console.error('[SSO] Error response:', errorData)
      throw new Error(`Failed to generate SSO token: ${errorData.message || response.statusText}`)
    }

    const data = await response.json()
    console.log('[SSO] Success! Redirect URL:', data.redirect_url)
    return data.redirect_url
  } catch (error: any) {
    console.error('[SSO] Exception caught:', error)
    throw new AuthError({
      message: error?.message || 'SSO token generation failed',
      status: error?.status || 500,
      data: error?.data || {},
    })
  }
}